import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RemoteSearchSelect } from '@/components/ui/remote-search-select';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type { EnCodeOption, ReferentialMapping, V2DataItem } from '../api/migration-v1-v2';

const NONE_VALUE = '__none__';

interface Props {
  /** Type de référentiel label-keyed : 'cycle' | 'niveau' | 'matiere'. */
  type: string;
  /** Ressource de nomenclature V2 (`cycles`, `niveaux`…). */
  v2Resource: string;
  /** Libellé de la colonne cible (ex: "Cycle V2"). */
  v2Label: string;
  /** Mot au singulier pour les libellés UI (ex: "cycle"). */
  noun: string;
  /**
   * Active la colonne « Code EN » (Éducation Nationale), mutualisée entre
   * l'assistant et la migration : `mef` = code MEF (mapping niveau→MEF),
   * `matiere` = code BCN (mapping matière→BCN). Absent = pas de colonne EN.
   */
  enCodeKind?: 'mef' | 'matiere';
}

interface EditRow {
  key: string;
  sourceLabel: string;
  targetCode: string | null;
  targetLabel: string | null;
  enCode: string | null;
}

/** Formate une option EN pour l'affichage (badge langue vivante inclus). */
function enOptionLabel(o: EnCodeOption): string {
  if (o.isLangueVivante) {
    const rang = o.rang ? `LV${o.rang}` : 'LV';
    const type = (o.typeLangueRequis ?? [])
      .map((t) => (t === 'LVE' ? 'étrangère' : t === 'LVR' ? 'régionale' : t))
      .join('/');
    return `${o.label} · ${rang}${type ? ` ${type}` : ''} (${o.code})`;
  }
  return `${o.label} (${o.code})`;
}

/**
 * Éditeur du **catalogue de défauts globaux** pour un type label-keyed
 * (cycles / niveaux). Indexé par libellé : on saisit "CP", "GS"… et on associe
 * chaque libellé à une valeur normalisée V2. Ces défauts sont ensuite appliqués
 * automatiquement à toutes les écoles (cf. `LotMappingDialog`).
 */
export function GlobalLabelMappingsTab({ type, v2Resource, v2Label, noun, enCodeKind }: Props) {
  const enSearch = enCodeKind === 'mef'
    ? migrationV1V2Api.searchEnMefs
    : enCodeKind === 'matiere'
      ? migrationV1V2Api.searchEnMatieres
      : null;
  const enColLabel = enCodeKind === 'mef' ? 'Code MEF' : 'Code BCN';
  const [rows, setRows] = useState<EditRow[]>([]);
  const [v2Items, setV2Items] = useState<V2DataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const counter = useRef(0);

  const newKey = () => `row-${counter.current++}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mappings, items] = await Promise.all([
        migrationV1V2Api.getMappings(type),
        migrationV1V2Api.getV2DataResource(v2Resource),
      ]);
      setV2Items(items);
      setRows(
        mappings.map((m) => ({
          key: newKey(),
          sourceLabel: m.sourceLabel,
          targetCode: m.targetCode,
          targetLabel: m.targetLabel,
          enCode: m.enCode ?? null,
        }))
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [type, v2Resource]);

  useEffect(() => {
    load();
  }, [load]);

  /** Normalise un item de nomenclature V2 en `{code, label}`. */
  const v2Options = useMemo(
    () =>
      v2Items
        .map((it) => {
          const code = String(it.code ?? it.id ?? '').trim();
          const label = String(
            it.libelle ?? it.libelleLong ?? it.libelleCourt ?? it.label ?? it.name ?? code
          ).trim();
          return { code, label };
        })
        .filter((o) => o.code !== ''),
    [v2Items]
  );

  const v2LabelForCode = (code: string | null): string | null => {
    if (!code) return null;
    return v2Options.find((o) => o.code === code)?.label ?? null;
  };

  const updateRow = (key: string, patch: Partial<EditRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setSaved(false);
  };

  const addRow = () => {
    setRows((prev) => [...prev, { key: newKey(), sourceLabel: '', targetCode: null, targetLabel: null, enCode: null }]);
    setSaved(false);
  };

  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload: ReferentialMapping[] = rows
        .filter((r) => r.sourceLabel.trim() !== '')
        .map((r) => ({
          type,
          // sourceId dérivé du libellé côté backend (clé normalisée).
          sourceId: '',
          sourceLabel: r.sourceLabel.trim(),
          targetCode: r.targetCode,
          targetLabel: r.targetCode ? r.targetLabel ?? v2LabelForCode(r.targetCode) : null,
          enCode: enCodeKind ? r.enCode : undefined,
        }));
      await migrationV1V2Api.saveMappings(type, payload);
      await load();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const mappedCount = rows.filter((r) => r.sourceLabel.trim() && r.targetCode).length;
  const totalCount = rows.filter((r) => r.sourceLabel.trim()).length;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="space-y-4" style={{ textTransform: 'none' }}>
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Défauts globaux appliqués à toutes les écoles. Un libellé V1 reconnu (ex.
          « CP ») est pré-mappé automatiquement ; l'école peut surcharger ponctuellement.
        </p>
        <div className="flex items-center gap-3 shrink-0">
          {totalCount > 0 && (
            <Badge variant={mappedCount === totalCount ? 'success' : 'warning'}>
              {mappedCount}/{totalCount} mappés
            </Badge>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : saved ? (
              <Check className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saved ? 'Sauvegardé' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
      )}

      {v2Options.length === 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          Nomenclature V2 vide ou non chargée — vérifier qu'au moins une école dispose
          d'un tenant V2 déployé.
        </div>
      )}

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: enCodeKind ? '30%' : '40%' }} />
            <col style={{ width: enCodeKind ? '34%' : '46%' }} />
            {enCodeKind && <col style={{ width: '26%' }} />}
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800">
              <th className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                Libellé V1
              </th>
              <th className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                {v2Label}
              </th>
              {enCodeKind && (
                <th className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                  {enColLabel} <span className="text-gray-400 font-normal">(Éd. Nationale)</span>
                </th>
              )}
              <th className="px-3 py-2.5 text-right font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                <td className="px-3 py-2 align-middle">
                  <Input
                    value={row.sourceLabel}
                    placeholder={`Libellé ${noun} V1 (ex. CP)`}
                    onChange={(ev) => updateRow(row.key, { sourceLabel: ev.target.value })}
                  />
                </td>
                <td className="px-3 py-2 align-middle">
                  <Select
                    value={row.targetCode ?? NONE_VALUE}
                    onValueChange={(v) => {
                      const code = v === NONE_VALUE ? null : v;
                      updateRow(row.key, {
                        targetCode: code,
                        targetLabel: code ? v2LabelForCode(code) : null,
                      });
                    }}
                  >
                    <SelectTrigger className={!row.targetCode ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/10' : ''}>
                      <SelectValue>
                        {row.targetCode ? (
                          v2LabelForCode(row.targetCode) ?? row.targetCode
                        ) : (
                          <span className="text-gray-400">(non mappé)</span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>
                        <span className="text-gray-400">(non mappé)</span>
                      </SelectItem>
                      {v2Options.map((o) => (
                        <SelectItem key={o.code} value={o.code}>
                          <span>
                            {o.label}{' '}
                            <span className="text-gray-400 text-xs font-mono">{o.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                {enCodeKind && enSearch && (
                  <td className="px-3 py-2 align-middle">
                    <RemoteSearchSelect
                      value={row.enCode ?? ''}
                      selectedLabel={row.enCode}
                      search={(q) =>
                        enSearch(q).then((opts) =>
                          opts.map((o) => ({ code: o.code, label: enOptionLabel(o) }))
                        )
                      }
                      onSelect={(code) => updateRow(row.key, { enCode: code || null })}
                      placeholder={enCodeKind === 'mef' ? 'Rechercher un MEF…' : 'Rechercher une matière…'}
                    />
                  </td>
                )}
                <td className="px-3 py-2 text-right align-middle">
                  <Button size="sm" variant="ghost" onClick={() => removeRow(row.key)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={enCodeKind ? 4 : 3} className="px-3 py-8 text-center text-gray-500">
                  Aucun défaut global. Ajouter un libellé pour commencer.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Button variant="outline" onClick={addRow}>
        <Plus className="h-4 w-4" />
        Ajouter un libellé
      </Button>
    </div>
  );
}
